import React from "react";
import { cn } from "utils";
import { Icon } from "@ospweb/sdk-ui";
import MemberStatusCard, {
  type ChatMemberStatus,
  type ChatMemberStatusCardProps,
  type ChatMemberCardVariant,
} from "./MemberStatusCard";
import {
  EdgenModalCreateAble,
  type EdgenModalCreateAbleProps,
} from "@components/EdgenModal";

type MemberStatusCardModalVariant = ChatMemberCardVariant;

export interface MemberStatusCardModalCreateAbleProps extends Omit<
  ChatMemberStatusCardProps,
  "onPrimaryClick" | "onSecondaryClick"
> {
  variant?: MemberStatusCardModalVariant;
  modalProps?: EdgenModalCreateAbleProps;
  onClose?: () => void | Promise<void>;
  onPrimaryClick?: () => void | Promise<void>;
  onSecondaryClick?: () => void | Promise<void>;
}

type MemberStatusCardModalResultAction =
  "close" | "primaryClick" | "secondaryClick";

export interface MemberStatusCardModalResult {
  action: MemberStatusCardModalResultAction;
  memberStatus: ChatMemberStatus;
}

export const MemberStatusCardModalCreateAble = async (
  props: MemberStatusCardModalCreateAbleProps,
): Promise<MemberStatusCardModalResult> => {
  const {
    variant = "member-status",
    modalProps,
    onClose,
    onPrimaryClick,
    onSecondaryClick,
    ...cardProps
  } = props;

  const resolvedCardProps: ChatMemberStatusCardProps = {
    ...cardProps,
    variant,
  };

  return new Promise((resolve) => {
    const closeAndResolve = async (
      action: MemberStatusCardModalResultAction,
    ) => {
      if (action === "close") {
        await onClose?.();
      }
      EdgenModalCreateAble.end(false);
      resolve({ action, memberStatus: resolvedCardProps.memberStatus });
    };

    const handlePrimaryClick = async () => {
      await onPrimaryClick?.();
      EdgenModalCreateAble.end(false);
      resolve({
        action: "primaryClick",
        memberStatus: resolvedCardProps.memberStatus,
      });
    };

    const handleSecondaryClick = async () => {
      await onSecondaryClick?.();
      EdgenModalCreateAble.end(false);
      resolve({
        action: "secondaryClick",
        memberStatus: resolvedCardProps.memberStatus,
      });
    };

    EdgenModalCreateAble.call({
      children: (
        <div className={cn("relative w-full p-6")}>
          <Icon
            data-openreplay-label="chatbox_memberstatuscardmodal_close"
            name="close"
            size={24}
            onClick={() => {
              void closeAndResolve("close");
            }}
            className="text-content_primary absolute right-3 top-3 cursor-pointer"
          />
          <MemberStatusCard
            {...resolvedCardProps}
            onPrimaryClick={handlePrimaryClick}
            onSecondaryClick={handleSecondaryClick}
            className="border-none bg-transparent p-0"
            titleClassName="!headline_regular_s"
            descriptionClassName="mt-2"
            btnContainerClassName="mt-6 flex-row-reverse gap-2"
            showInModal={true}
            button={{
              primary: {
                ...resolvedCardProps.button?.primary,
                className: cn(
                  "sm_button button_m",
                  resolvedCardProps.button?.primary?.className,
                ),
              },
              secondary: {
                ...resolvedCardProps.button?.secondary,
                className: cn(
                  "sm_button button_m",
                  resolvedCardProps.button?.secondary?.className,
                ),
              },
            }}
          />
        </div>
      ),
      containerClassName: "!md:w-[420px] w-[320px] rounded-2xl",
      contentStyle: {
        borderRadius: 16,
        width: "100%",
        padding: 0,
      },
      centered: true,
      destroyOnClose: true,
      showClose: false,
      onCancel: () => {
        void closeAndResolve("close");
      },
      ...modalProps,
    } as any);
  });
};
